/* eslint-disable @typescript-eslint/camelcase */
import { createAssets, updateAsset } from '@api/v4/assets/assets';
import bulkTag, { BulkTagResponse } from '@api/v4/bulk_actions/tag';
import updateAttachment, { deleteAttachment } from '@api/v4/attachments';
import { createCustomFields } from '@api/v4/assets/custom_fields';
import { deleteCustomFieldValue, updateCustomFieldValue } from '@api/v4/custom_fields/custom_fields';
import { ApiDatumResponse } from '@api/v4/ApiResponseTypes';
import { AssetResponse, AssetsResponseObject } from '@api/v4/assets/assetTypes';
import { CreateCustomFieldParams, CustomFieldMapsValue, CreateCustomFieldsResponse } from '@api/v4/assets/customFieldTypes';
import { AttachmentResponseObject } from '@api/v4/attachments/attachmentTypes';
import { Locale } from '@components/common/language_menu/languagesMap';
import { AssetChanges } from './change_detection/assetChangeEngine';
import { SubmitTagsData } from './change_detection/tagChangeEngine';
import { UpdatedAttachmentData } from './change_detection/attachmentChangeEngine';
import { FlatCustomFieldMaps } from './EditTabTypes';


export enum Submit {
  DeletedAsset,
  DeletedAttachment,
  DeletedCustomFieldValue,
  NewAsset,
  NewCustomFields,
  Tags,
  UpdatedAsset,
  UpdatedAttachment,
  UpdatedCustomField
}

type DeleteAttachmentData = string;

export interface SubmissionData {
  action: Submit;
  data:
    | Partial<AssetChanges>
    | CreateCustomFieldParams[]
    | FlatCustomFieldMaps
    | SubmitTagsData
    | DeleteAttachmentData
    | UpdatedAttachmentData;
}

interface CreateAssetData {
  assetChanges: Partial<AssetChanges>;
  section_key: string;
}

interface SubmissionResult<T> {
  action: Submit;
  success: boolean;
  results?: T;
}

type SubmissionPromise<T> = Promise<SubmissionResult<T>>;

export interface SubmitData {
  failures: Submit[] | null;
  successes: { action: Submit; results: any }[];
  assetKey?: string;
}

const createAssetAsync = async (
  { assetChanges, section_key }: CreateAssetData,
  updateFetchControllers: () => void
): Promise<SubmissionResult<AssetsResponseObject>> => {
  let url = "/api/v4/";
  switch (BFG.resource.type) {
    case 'collection':
      url += `collections/${BFG.resource.key}/assets`;
      break;
    case 'brandfolder':
    default:
      url += `brandfolders/${BFG.resource.key}/assets`;
      break;
  }
  const options = {
    url,
    section_key,
    data: {
      attributes: [assetChanges]
    }
  };

  try {
    const results = await createAssets(options, updateFetchControllers);
    return {
      action: Submit.UpdatedAsset,
      success: true,
      results: results.data[0]
    };
  } catch (e) {
    return {
      action: Submit.UpdatedAsset,
      success: false
    };
  }
};

const submitAssetUpdate = async (
  assetKey: string,
  assetChanges: Partial<AssetChanges>,
  updateFetchControllers: () => void,
  ugtLocale: Locale | '',
): Promise<SubmissionResult<AssetResponse>> => {
  const options = {
    assetKey,
    attributes: assetChanges,
    params: { ugt_locale: ugtLocale, queue_priority: "high" },
  };
  try {
    const results = await updateAsset(options, updateFetchControllers);
    return {
      action: Submit.UpdatedAsset,
      success: true,
      results
    };
  } catch (e) {
    return {
      action: Submit.UpdatedAsset,
      success: false
    };
  }
};

const submitNewCustomFields = async (
  assetKey: string,
  createdCustomFields: CreateCustomFieldParams[],
  ugtLocale: Locale | ''
): Promise<SubmissionResult<CreateCustomFieldsResponse>> => {
  try {
    const results = await createCustomFields(
      { assetKey, createdCustomFields },
      { ugt_locale: ugtLocale }
    );
    return {
      action: Submit.NewCustomFields,
      success: true,
      results
    };
  } catch (e) {
    return {
      action: Submit.NewCustomFields,
      success: false
    };
  }
};

const submitUpdatedCustomField = async (
  customField: FlatCustomFieldMaps,
  assetKey: string,
  ugtLocale: Locale | ''
): Promise<SubmissionResult<CustomFieldMapsValue>> => {
  try {
    let results;
    // if existing value in db, update it
    if (customField.value.id) {
      results = await updateCustomFieldValue(
        { key: customField.value.id, value: customField.value.value },
        { ugt_locale: ugtLocale }
      );
    } else {
      const newCustomFieldValue = {
        key: customField.key.name,
        value: customField.value.value
      };
      results = await createCustomFields(
        { assetKey, createdCustomFields: [newCustomFieldValue] },
        { ugt_locale: ugtLocale }
      );
    }
    return {
      action: Submit.UpdatedCustomField,
      success: true,
      results
    };
  } catch (e) {
    return {
      action: Submit.UpdatedCustomField,
      success: false
    };
  }
};

const submitTags = async (
  assetKey: string,
  { initialTags, editedTags }: SubmitTagsData,
  updateFetchControllers: () => void,
  ugtLocale: Locale | '',
): Promise<SubmissionResult<BulkTagResponse>> => {
  const data = {
    asset_keys: [assetKey],
    bulk_tag: {
      existing_tag_names_array: initialTags.map((tag) => tag.name),
      submitted_tag_names_array: editedTags.map((tag) => tag.name)
    }
  };
  try {
    await bulkTag(data, updateFetchControllers, ugtLocale);
    return {
      action: Submit.Tags,
      success: true,
    };
  } catch (e) {
    return {
      action: Submit.Tags,
      success: false
    };
  }
};

const submitUpdateAttachment = async (
  { key, url, filename, position }: UpdatedAttachmentData
): Promise<
  SubmissionResult<
    ApiDatumResponse<AttachmentResponseObject>
  >
> => {
  try {
    const results = await updateAttachment({ attachmentKey: key, data: { attributes: { url, filename, position } } });
    return {
      action: Submit.UpdatedAttachment,
      success: true,
      results
    };
  } catch (e) {
    return {
      action: Submit.UpdatedAttachment,
      success: false
    };
  }
};

const submitDeleteCustomFieldValue = async (key: string, ugtLocale: Locale | ''): Promise<SubmissionResult<string>> => {
  try {
    await deleteCustomFieldValue(key, { ugt_locale: ugtLocale });
    return {
      action: Submit.DeletedCustomFieldValue,
      success: true,
      results: key
    };
  } catch (e) {
    return {
      action: Submit.DeletedCustomFieldValue,
      success: false
    };
  }
};

const submitDeleteAttachment = async (attachmentKey: string): Promise<SubmissionResult<string>> => {
  try {
    await deleteAttachment(attachmentKey);
    return {
      action: Submit.DeletedAttachment,
      success: true,
      results: attachmentKey
    };
  } catch (e) {
    return {
      action: Submit.DeletedAttachment,
      success: false
    };
  }
};

const submit = async (
  assetKey: string,
  updateFetchControllers: () => void,
  requiredSubmissions: SubmissionData[],
  ugtLocale: Locale | '',
  section_key?: string,
): Promise<SubmitData> => {
  const isNewAsset = assetKey === '';
  let reliableAssetKey = assetKey;
  let remainingSubmissions = [...requiredSubmissions];
  const submissionMap = {
    [Submit.UpdatedAsset]:
      (data: Partial<AssetChanges>): SubmissionPromise<AssetResponse> => submitAssetUpdate(reliableAssetKey, data, updateFetchControllers, ugtLocale),
    [Submit.NewCustomFields]:
      (data: CreateCustomFieldParams[]): SubmissionPromise<CreateCustomFieldsResponse> => submitNewCustomFields(reliableAssetKey, data, ugtLocale),
    [Submit.UpdatedCustomField]:
      (data: FlatCustomFieldMaps): SubmissionPromise<CustomFieldMapsValue> => submitUpdatedCustomField(data, reliableAssetKey, ugtLocale),
    [Submit.DeletedCustomFieldValue]:
      (data: string): SubmissionPromise<string> => submitDeleteCustomFieldValue(data, ugtLocale),
    [Submit.Tags]:
      (data: SubmitTagsData): SubmissionPromise<BulkTagResponse> => submitTags(reliableAssetKey, data, updateFetchControllers, ugtLocale),
    [Submit.DeletedAttachment]:
      (data: DeleteAttachmentData): SubmissionPromise<string> => submitDeleteAttachment(data),
    [Submit.UpdatedAttachment]:
      (data: UpdatedAttachmentData): SubmissionPromise<ApiDatumResponse<AttachmentResponseObject>> => submitUpdateAttachment(data)
  };

  let reliableAssetKeyPromise: Promise<void> = new Promise((resolve) => resolve());
  const submissionErrors = [];
  const successResults: { action: Submit; results: any }[] = [];
  if (isNewAsset) {
    const assetChanges = remainingSubmissions.find((s) => s.action === Submit.UpdatedAsset).data as Partial<AssetChanges>;
    const updateAssetActionIndex = remainingSubmissions.findIndex((s) => s.action === Submit.UpdatedAsset);
    remainingSubmissions.splice(updateAssetActionIndex, 1);
    reliableAssetKeyPromise = createAssetAsync(
      { assetChanges, section_key },
      updateFetchControllers
    ).then((submissionResponse) => {
      if (submissionResponse.success) {
        reliableAssetKey = submissionResponse.results.id;
        successResults.push({ action: submissionResponse.action, results: submissionResponse.results });
      } else {
        submissionErrors.push(Submit.UpdatedAsset);
      }
    });
  }

  if (submissionErrors.length > 0) {
    // asset creation has failed, let's not try to submit anything else
    return {
      successes: successResults,
      failures: submissionErrors,
      assetKey: reliableAssetKey
    };
  }

  const handleAttachmentDeleteSubmission = async (): Promise<void> => {
    const deleteAttachmentSubmissions = remainingSubmissions.filter(({ action }) => action === Submit.DeletedAttachment);
    if (deleteAttachmentSubmissions.length === 0) return;

    const deleteRequests = (): SubmissionPromise<string>[] => deleteAttachmentSubmissions.map(({ data }) => (
      submissionMap[Submit.DeletedAttachment](data as DeleteAttachmentData)
    ));
    const deleteAttachmentResults = await Promise.all(deleteRequests());
    deleteAttachmentResults.forEach((deleteAttachmentResult) => {
      if (deleteAttachmentResult.success) {
        const { action, results } = deleteAttachmentResult;
        successResults.push({ action, results });
      } else {
        submissionErrors.push(deleteAttachmentResult.action);
      }
    });
    remainingSubmissions = remainingSubmissions.filter(({ action }) => action !== Submit.DeletedAttachment);
  };

  const handleAssetUpdateSubmission = async (): Promise<void> => {
    const updateAssetSubmission = remainingSubmissions.find(({ action }) => action === Submit.UpdatedAsset);
    if (!updateAssetSubmission) return;

    const updateAssetResult = await submissionMap[Submit.UpdatedAsset](updateAssetSubmission.data as Partial<AssetChanges>);
    if (updateAssetResult.success) {
      const { action, results } = updateAssetResult;
      successResults.push({ action, results });
    } else {
      submissionErrors.push(updateAssetResult.action);
    }
    remainingSubmissions = remainingSubmissions.filter(({ action }) => action !== Submit.UpdatedAsset);
  };

  const handleAttachmentUpdateSubmission = async (): Promise<void> => {
    const updateAttachmentSubmissions = remainingSubmissions.filter(({ action }) => action === Submit.UpdatedAttachment);
    if (updateAttachmentSubmissions.length === 0) return;

    for (let i = 0; i < updateAttachmentSubmissions.length; i += 1) {
      const { data } = updateAttachmentSubmissions[i];
      // eslint-disable-next-line no-await-in-loop
      const updateAttachmentResult = await submissionMap[Submit.UpdatedAttachment](data as UpdatedAttachmentData);
      if (updateAttachmentResult.success) {
        const { action, results } = updateAttachmentResult;
        successResults.push({ action, results });
      } else {
        submissionErrors.push(updateAttachmentResult.action);
      }
    }

    remainingSubmissions = remainingSubmissions.filter(({ action }) => action !== Submit.UpdatedAttachment);
  };

  const handleOrderedSubmissions = async (): Promise<void> => {
    if (!remainingSubmissions.find(({ action }) => action === Submit.UpdatedAttachment)) return;
    await handleAttachmentDeleteSubmission();
    // we could do an early return here if there were any errors
    // and not even try any other submissions but with the messaging that returns for errors
    // which implies that some changes were unsuccessful implies that the positioning of attachments could be saved incorrectly
    // so I think it's fair to try and submit the other things and live with the fallout of slightly off attachment positioning
    await handleAssetUpdateSubmission();
    await handleAttachmentUpdateSubmission();
  };

  const requests = (): Promise<any>[] => (
    remainingSubmissions.map(({ action, data }) => submissionMap[action](data))
  );

  try {
    await reliableAssetKeyPromise;
    await handleOrderedSubmissions();
    const submissionResults = await Promise.all(requests());
    submissionResults.forEach((submissionResult) => {
      if (submissionResult.success) {
        successResults.push({ action: submissionResult.action, results: submissionResult.results });
      } else {
        submissionErrors.push(submissionResult.action);
      }
    });

    return {
      successes: successResults,
      failures: submissionErrors,
      assetKey: reliableAssetKey
    };
  } catch (e) {
    throw e;
  }
};

export default submit;
